import { useNavigate } from "react-router-dom";
import { Sparkles, Radar, Receipt, MapPin, Quote } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { PrimaryButton, SecondaryButton } from "@/components/brand";
import { useLang, t, withLang } from "@/lib/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1687766011004-8cb676b03bce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const VALUE_PROPS = [
  {
    icon: Sparkles,
    title: { en: "AI finds the fastest route", fr: "L'IA trouve le trajet le plus rapide" },
    body: {
      en: "Routa's routing engine sequences stops and matches drivers automatically, so every order takes the most efficient path from pickup to door.",
      fr: "Le moteur de routage de Routa séquence les arrêts et jumelle les livreurs automatiquement — chaque commande emprunte le trajet le plus efficace, du ramassage à la porte.",
    },
  },
  {
    icon: Radar,
    title: { en: "Real-time order and driver tracking", fr: "Suivi en temps réel des commandes et livreurs" },
    body: {
      en: "Watch every delivery move from pickup to drop off. Live status, assigned driver, and ETA on one screen — plus shareable tracking links for your customers.",
      fr: "Suivez chaque livraison du ramassage à la porte. Statut en direct, livreur assigné et heure d'arrivée sur un seul écran — plus un lien de suivi partageable pour vos clients.",
    },
  },
  {
    icon: Receipt,
    title: { en: "Simple, predictable pricing", fr: "Une tarification simple et prévisible" },
    body: {
      en: "Straightforward monthly plans that scale with your volume. No surge, no hidden platform fees, no surprises.",
      fr: "Des forfaits mensuels clairs qui évoluent avec votre volume. Aucun tarif de pointe, aucun frais caché, aucune surprise.",
    },
  },
  {
    icon: MapPin,
    title: { en: "A local driver network", fr: "Un réseau de livreurs locaux" },
    body: {
      en: "Professional operators who know the region. Launching in Metro Vancouver and expanding across Canada.",
      fr: "Des opérateurs professionnels qui connaissent la région. Lancement à Vancouver métropolitain et expansion à travers le Canada.",
    },
  },
];

const STEPS = [
  { n: "01",
    title: { en: "Create your account", fr: "Créez votre compte" },
    body: { en: "Sign up in minutes, add your business details, and choose the plan that fits your volume.",
             fr: "Inscrivez-vous en quelques minutes, ajoutez les détails de votre entreprise et choisissez le forfait qui convient à votre volume." } },
  { n: "02",
    title: { en: "Connect your orders", fr: "Connectez vos commandes" },
    body: { en: "Add your storefront and pickup locations, then send delivery requests as orders come in.",
             fr: "Ajoutez votre boutique et vos lieux de ramassage, puis envoyez les demandes de livraison au fil des commandes." } },
  { n: "03",
    title: { en: "AI dispatches every drop", fr: "L'IA répartit chaque livraison" },
    body: { en: "Routa matches each request to a driver and routes it the fastest way. You track it live.",
             fr: "Routa jumelle chaque demande à un livreur et l'achemine par le trajet le plus rapide. Vous suivez en direct." } },
];

// Reference customer from our closed pilot (Northside Provisions — the same
// merchant used across the seed dataset so operators can see the whole story
// in one place). Numbers are representative pilot figures, not audited public
// claims; ops will swap in a public case study once it's signed off.
const CASE_STUDY = {
  eyebrow: { en: "[03] Reference customer", fr: "[03] Client de référence" },
  brand: { en: "Northside Provisions", fr: "Northside Provisions" },
  vertical: { en: "Independent grocer · Vancouver, BC", fr: "Épicerie indépendante · Vancouver, C.-B." },
  headline: {
    en: "\"We stopped losing orders to routing chaos the day we switched to Routa.\"",
    fr: "« On a arrêté de perdre des commandes dans le chaos de la répartition dès qu'on est passés à Routa. »",
  },
  quote: {
    en: "Routa gave us one dispatch view for every store, live driver ETAs our customers actually trust, and an AI that quietly sequences stops in the background. Our team stopped calling drivers to reroute — the platform does it.",
    fr: "Routa nous a donné une seule vue de répartition pour chaque magasin, des heures d'arrivée que nos clients croient vraiment, et une IA qui séquence les arrêts en arrière-plan. Notre équipe a arrêté d'appeler les livreurs pour changer les trajets — la plateforme s'en charge.",
  },
  attribution: {
    en: "Dana Osei · Head of Operations, Northside Provisions",
    fr: "Dana Osei · Cheffe des opérations, Northside Provisions",
  },
  metrics: [
    { value: "2×", label: { en: "Deliveries per driver, per shift", fr: "Livraisons par livreur, par quart" } },
    { value: "-34%", label: { en: "Missed delivery windows", fr: "Fenêtres de livraison manquées" } },
    { value: "< 1 min", label: { en: "Median assign-to-notify time", fr: "Délai médian d'assignation à notification" } },
    { value: "2 stores", label: { en: "Rolled onto Routa in one week", fr: "Boutiques activées sur Routa en une semaine" } },
  ],
};

export default function MerchantLanding() {
  const navigate = useNavigate();
  const lang = useLang();
  return (
    <div className="min-h-screen bg-white">
      <MarketingNav />

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-4 mb-8">
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
                {t(lang, { en: "[01] For merchants", fr: "[01] Pour marchands" })}
              </span>
              <span className="h-px w-16 bg-gray-300" />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
                {t(lang, { en: "Vancouver · Canada", fr: "Vancouver · Canada" })}
              </span>
            </div>
            <h1 className="headline text-5xl lg:text-[4.5rem] text-[#0a0a0a]">
              {t(lang, {
                en: <>The fastest way to get your products to the <span className="text-orange-500">door.</span></>,
                fr: <>La façon la plus rapide de livrer vos produits <span className="text-orange-500">à la porte.</span></>,
              })}
            </h1>
            <p className="mt-7 text-base text-gray-500 max-w-md leading-relaxed">
              {t(lang, {
                en: "Routa is AI-powered last-mile delivery software. Dispatch orders, manage drivers, and let intelligent routing find the most efficient path for every delivery — with real-time tracking end to end.",
                fr: "Routa est une plateforme de livraison du dernier kilomètre propulsée par l'IA. Répartissez les commandes, gérez vos livreurs, et laissez le routage intelligent trouver le trajet le plus efficace pour chaque livraison — avec un suivi en direct de bout en bout.",
              })}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryButton orange arrow data-testid="merchant-hero-cta" onClick={() => navigate(withLang("/register", lang))}>
                {t(lang, { en: "Get Started", fr: "Commencer" })}
              </PrimaryButton>
              <SecondaryButton data-testid="merchant-hero-login" onClick={() => navigate(withLang("/pricing", lang))}>
                {t(lang, { en: "View pricing", fr: "Voir les tarifs" })}
              </SecondaryButton>
            </div>
          </div>

          <div className="relative aspect-[4/5] w-full overflow-hidden animate-fade-up" style={{ animationDelay: "120ms" }}>
            <img src={HERO_IMG} alt="Merchant fulfillment" className="absolute inset-0 h-full w-full object-cover grayscale" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/85 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/60">
                {t(lang, { en: "Merchant node", fr: "Nœud marchand" })}
              </div>
              <div className="font-mono text-sm text-white mt-1">
                {t(lang, { en: "AI dispatch, on demand", fr: "Répartition par IA, à la demande" })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="bg-gray-100 py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-10">
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
              {t(lang, { en: "[02] Why Routa", fr: "[02] Pourquoi Routa" })}
            </span>
            <span className="h-px flex-1 bg-gray-200" />
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
              {t(lang, { en: "Built for operators", fr: "Fait pour les opérateurs" })}
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
            {VALUE_PROPS.map((v) => (
              <div key={t("en", v.title)} className="bg-white p-8">
                <v.icon className="h-6 w-6 text-orange-500" strokeWidth={1.8} />
                <h3 className="mt-6 text-xl font-bold text-[#0a0a0a]">{t(lang, v.title)}</h3>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">{t(lang, v.body)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case study — Northside Provisions */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20" data-testid="merchant-case-study">
        <div className="flex items-center gap-4 mb-10">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
            {t(lang, CASE_STUDY.eyebrow)}
          </span>
          <span className="h-px flex-1 bg-gray-200" />
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
            {t(lang, { en: "Pilot results", fr: "Résultats du pilote" })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-start">
          <div>
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400">
              {t(lang, CASE_STUDY.brand)}
            </div>
            <div className="text-sm text-gray-500 mt-1">{t(lang, CASE_STUDY.vertical)}</div>
            <div className="mt-6 flex items-start gap-4">
              <Quote className="h-8 w-8 text-orange-500 shrink-0 -mt-1" />
              <h2 className="headline text-3xl lg:text-4xl text-[#0a0a0a] leading-tight">
                {t(lang, CASE_STUDY.headline)}
              </h2>
            </div>
            <p className="mt-6 text-base text-gray-600 leading-relaxed max-w-2xl">
              {t(lang, CASE_STUDY.quote)}
            </p>
            <div className="mt-6 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">
              {t(lang, CASE_STUDY.attribution)}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-px bg-gray-200 border border-gray-200">
            {CASE_STUDY.metrics.map((m, i) => (
              <div key={i} className="bg-white p-6 lg:p-7" data-testid={`case-metric-${i}`}>
                <div className="text-3xl lg:text-4xl font-bold tracking-tight text-[#0a0a0a]">{m.value}</div>
                <div className="mt-2 text-xs text-gray-500 leading-snug">{t(lang, m.label)}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-10 text-xs text-gray-400 max-w-2xl">
          {t(lang, {
            en: "Pilot metrics from Northside Provisions' first 8 weeks on Routa (2026). Results depend on volume, region, and existing dispatch process.",
            fr: "Indicateurs du pilote des 8 premières semaines de Northside Provisions sur Routa (2026). Les résultats varient selon le volume, la région et le processus de répartition existant.",
          })}
        </p>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-20">
        <div className="flex items-center gap-4 mb-12">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
            {t(lang, { en: "[04] How it works", fr: "[04] Comment ça marche" })}
          </span>
          <span className="h-px flex-1 bg-gray-200" />
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
            {t(lang, { en: "Three steps", fr: "Trois étapes" })}
          </span>
        </div>
        <div className="grid md:grid-cols-3 gap-10">
          {STEPS.map((s) => (
            <div key={s.n}>
              <div className="font-mono text-sm text-orange-500 mb-4">{s.n}</div>
              <h3 className="text-xl font-bold text-[#0a0a0a]">{t(lang, s.title)}</h3>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">{t(lang, s.body)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#0a0a0a] py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/40 mb-5">
              {t(lang, { en: "[05] Get started", fr: "[05] Commencer" })}
            </div>
            <h2 className="headline text-3xl lg:text-5xl text-white">
              {t(lang, { en: "Deliver smarter. Start today.", fr: "Livrez intelligemment. Commencez aujourd'hui." })}
            </h2>
          </div>
          <PrimaryButton orange arrow data-testid="merchant-closing-cta" onClick={() => navigate(withLang("/register", lang))} className="shrink-0">
            {t(lang, { en: "Get Started", fr: "Commencer" })}
          </PrimaryButton>
        </div>
      </section>

      <Footer />
    </div>
  );
}
